import { useEffect, useState } from "react";
import { History, AlertCircle, Info, Clock, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, apiErrorMessage, formatRupiah } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";

export default function Riwayat() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pot, setPot] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [hist, potRes] = await Promise.all([
          api.get("/attendance/history"),
          api.get("/attendance/potongan-absensi-preview").catch(() => null),
        ]);
        setRows(hist.data);
        setPot(potRes?.data || null);
      } catch (e) {
        toast.error(apiErrorMessage(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const isRealtime = pot?.mode === "realtime" && pot?.periode?.is_realtime;
  const hariTerlewati = Number(pot?.hari_kerja_terlewati || pot?.hari_kerja_periode || 0);
  const hariTotal = Number(pot?.hari_kerja_total_periode || 0);
  const capMaksTercapai = !!(
    pot?.breakdown &&
    pot.breakdown.nilai_sebelum_cap > pot.breakdown.cap_maksimal
  );

  return (
    <div className="p-4 sm:p-8 max-w-6xl mx-auto" data-testid="page-riwayat">
      <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-teal-700">
        Riwayat Absensi
      </div>
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 mt-1 leading-tight">
        Catatan Kehadiran Anda
      </h1>
      <p className="text-slate-500 text-sm mt-1">
        60 catatan terakhir, diurutkan dari yang terbaru.
      </p>

      {/* Ringkasan Potongan Absensi periode berjalan */}
      {pot && pot.periode ? (
        <div
          className="mt-6 rounded-xl border border-rose-200 bg-rose-50/50 p-4 sm:p-5"
          data-testid="riwayat-potongan-absensi-card"
        >
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-md bg-rose-100 border border-rose-200 text-rose-700 flex items-center justify-center shrink-0">
              <AlertCircle size={16} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-rose-700">
                  Estimasi Potongan Absensi — {pot.periode?.label || "Periode berjalan"}
                </div>
                {isRealtime ? (
                  <span
                    className="inline-flex items-center gap-1 rounded-full bg-rose-700 text-white text-[9px] font-bold uppercase tracking-widest px-2 py-0.5"
                    data-testid="riwayat-realtime-badge"
                  >
                    <Clock size={10} /> Real-time
                  </span>
                ) : null}
                {capMaksTercapai ? (
                  <span
                    className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-900 border border-amber-300 text-[9px] font-bold uppercase tracking-widest px-2 py-0.5"
                    data-testid="riwayat-cap-badge"
                  >
                    <ShieldCheck size={10} /> Cap Maksimal
                  </span>
                ) : null}
              </div>
              <div className="mt-1 flex flex-wrap items-baseline gap-x-4 gap-y-1">
                <div
                  className="font-display font-extrabold text-2xl text-rose-800 font-mono-clock"
                  data-testid="riwayat-potongan-nominal"
                >
                  {formatRupiah(pot.potongan_absensi || 0)}
                </div>
                <div className="text-xs text-slate-600" data-testid="riwayat-hari-kerja-info">
                  Hari kerja: <b>{hariTerlewati}</b>
                  {hariTotal > 0 && hariTotal !== hariTerlewati ? (
                    <> / <b>{hariTotal}</b></>
                  ) : null}
                  {" · "}
                  Hadir: <b>{pot.hari_hadir || 0}</b> · Terlambat:{" "}
                  <b>{pot.hari_terlambat || 0}</b> · Alpha:{" "}
                  <b>{pot.hari_alpha || 0}</b>
                </div>
              </div>

              {pot.breakdown ? (
                <div className="mt-3 rounded-md bg-white border border-rose-100 p-3 text-xs text-slate-700 leading-relaxed space-y-1">
                  <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 mb-1">
                    Rincian Perhitungan
                  </div>
                  <div>
                    <b>Dasar Potongan Absensi (Rupiah):</b>{" "}
                    <span className="font-mono-clock" data-testid="riwayat-dasar-rupiah">
                      {formatRupiah(pot.breakdown.dasar_total)}
                    </span>{" "}
                    <span className="text-slate-500">
                      (Σ {(pot.breakdown.dasar_items || []).map((it) => it.nama).join(", ") || "—"})
                    </span>
                  </div>
                  <div>
                    Alpha: <b>{pot.breakdown.hari_alpha}</b> hari ×{" "}
                    {pot.breakdown.persen_alpha}% ={" "}
                    <b>
                      {(pot.breakdown.hari_alpha * pot.breakdown.persen_alpha).toFixed(2)}%
                    </b>
                  </div>
                  <div>
                    Terlambat: <b>{pot.breakdown.hari_terlambat}</b> hari ×{" "}
                    {pot.breakdown.persen_terlambat}% ={" "}
                    <b>
                      {(pot.breakdown.hari_terlambat * pot.breakdown.persen_terlambat).toFixed(2)}
                      %
                    </b>
                  </div>
                  <div className="border-t border-slate-100 pt-1">
                    <b>Total % Potongan:</b>{" "}
                    <b data-testid="riwayat-total-persen">{pot.breakdown.persen_total}%</b>{" "}
                    × {formatRupiah(pot.breakdown.dasar_total)} ={" "}
                    <b className="font-mono-clock">
                      {formatRupiah(pot.breakdown.nilai_sebelum_cap)}
                    </b>
                  </div>
                  {capMaksTercapai ? (
                    <div
                      className="text-amber-800 font-semibold flex items-start gap-1.5 bg-amber-50 border border-amber-200 rounded-md p-2 mt-1.5"
                      data-testid="riwayat-cap-warning"
                    >
                      <Info size={12} className="mt-0.5 shrink-0" />
                      <span>
                        Nilai potongan menyentuh <b>Cap Maksimal</b> (= total dasar).
                        Dipotong maks: <b>{formatRupiah(pot.breakdown.cap_maksimal)}</b>.
                      </span>
                    </div>
                  ) : null}
                </div>
              ) : (
                <div className="mt-2 text-[11px] text-slate-500 italic">
                  Belum ada potongan absensi periode ini — tidak ada hari Alpha/Terlambat, atau
                  belum ada komponen ber-flag <b>Dasar Potongan Absensi</b> di profil payroll
                  Anda.
                </div>
              )}
              <div className="mt-2 text-[10px] text-slate-400 italic">{pot.catatan}</div>
            </div>
          </div>
        </div>
      ) : null}

      <div className="mt-8 rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Tanggal</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Zona</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Check-In</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Status Masuk</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Check-Out</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Status Pulang</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Keterangan</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-slate-500 py-10">
                    Memuat…
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-14">
                    <div className="mx-auto w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
                      <History size={22} />
                    </div>
                    <div className="mt-3 font-semibold text-slate-700">Belum ada catatan absensi</div>
                    <div className="text-xs text-slate-500 mt-1">
                      Lakukan Check-In pertama Anda di halaman Hari Ini.
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((r) => (
                  <TableRow key={r.id} data-testid={`history-row-${r.id}`} className="border-b border-slate-100 hover:bg-slate-50/60">
                    <TableCell className="font-semibold text-slate-800 whitespace-nowrap">{r.tanggal_lokal}</TableCell>
                    <TableCell className="text-slate-600 text-xs">{r.timezone}</TableCell>
                    <TableCell className="font-mono-clock text-slate-900">{r.check_in_lokal || "—"}</TableCell>
                    <TableCell><StatusBadge status={r.check_in_status} /></TableCell>
                    <TableCell className="font-mono-clock text-slate-900">{r.check_out_lokal || "—"}</TableCell>
                    <TableCell><StatusBadge status={r.check_out_status} /></TableCell>
                    <TableCell><StatusBadge status={r.absence_status} /></TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
